import { useState, useRef } from 'react';
import {
  Divider,
  Box,
  Flex,
  TextArea,
  Button,
  IconButton,
  Dialog,
  DialogContentContainer,
  Menu,
  MenuItem,
  Icon,
  Text,
  Label,
  Tooltip,
} from '@vibe/core';
import { shift } from '@floating-ui/react-dom';
// @ts-ignore
import { Heading } from '@vibe/core';
import { Attach, File, Image, Forum, Undo, CloseSmall, Update } from '@vibe/icons';
import { type PortalComment, type PortalCommentReply, type PortalCommentAttachment } from '@gorilla/portal-shared/src/comments';
import { RelativeTime } from '@gorilla/ui/src/components/RelativeTime';
import EmojiPicker from './EmojiPicker';
import { monday } from '../services/monday';

function CommenterName({ comment }: { comment: PortalComment | PortalCommentReply }) {
  const { metadata } = comment;

  if (metadata.type === 'from-client') {
    return (
      <span>
        <strong>{comment.client_item_name || 'unknown'}</strong>
        {metadata.name && metadata.name.trim().toLowerCase() !== comment.client_item_name?.trim().toLowerCase() ? (
          <span>&nbsp;({metadata.name})</span>
        ) : null}
      </span>
    );
  }

  return (
    <span>
      <strong>{comment.creator_name}</strong>
    </span>
  );
}

type UploadAttachmentsProps = {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isAddingComment: boolean;
};

function UploadAttachments({ fileInputRef, isAddingComment }: UploadAttachmentsProps) {
  const [_rerender, setRerender] = useState(false);
  const [attachmentsMenuOpen, setAttachmentsMenuOpen] = useState(false);

  return (
    <Flex>
      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <IconButton
          kind="secondary"
          size="medium"
          icon={Attach}
          disabled={isAddingComment}
          className={
            fileInputRef?.current?.files && Array.from(fileInputRef.current.files).length > 0
              ? 'client-chat-upload client-chat-upload--files-selected'
              : 'client-chat-upload'
          }
        />
        <input
          type="file"
          id="fileUpload"
          ref={fileInputRef}
          multiple
          onChange={() => {
            setRerender((prev) => !prev);
          }}
          style={{
            cursor: 'pointer',
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'block',
            width: '40px',
            height: '40px',
            opacity: 0,
          }}
        />
      </div>
      {fileInputRef?.current?.files && Array.from(fileInputRef.current.files).length > 0 ? (
        <Dialog
          content={
            <DialogContentContainer
              size="none"
              style={{
                overflow: 'hidden',
              }}
            >
              <Menu size="medium">
                {Array.from(fileInputRef?.current?.files || []).map((file, index) => (
                  <MenuItem
                    //key={index}
                    title={file.name}
                    icon={CloseSmall}
                    tooltipContent=""
                    tooltipShowDelay={1000000000}
                    onClick={() => {
                      setAttachmentsMenuOpen(false);

                      const dataTransfer = new DataTransfer();
                      const files = Array.from(fileInputRef.current?.files || []).filter((_, i) => i !== index);
                      files.forEach((file) => dataTransfer.items.add(file));
                      // @ts-ignore
                      fileInputRef.current.files = dataTransfer.files;
                      setRerender((prev) => !prev);
                    }}
                    className="customMenuItem"
                  />
                ))}
              </Menu>
            </DialogContentContainer>
          }
          middleware={[shift({ mainAxis: false })]}
          showTrigger={[]}
          hideWhenReferenceHidden
          open={attachmentsMenuOpen}
          instantShowAndHide
          tooltip
          moveBy={{ main: 10, secondary: 10 }}
          // @ts-ignore
          position="right-end"
          onClickOutside={() => {
            setAttachmentsMenuOpen(false);
          }}
        >
          <Button
            kind="secondary"
            disabled={isAddingComment}
            size="medium"
            leftFlat
            preventClickAnimation
            onClick={() => {
              setAttachmentsMenuOpen(!attachmentsMenuOpen);
            }}
          >
            {Array.from(fileInputRef.current.files).length} attachment{Array.from(fileInputRef.current.files).length > 1 ? 's' : ''}
          </Button>
        </Dialog>
      ) : null}
    </Flex>
  );
}

type AttachmentsProps = {
  attachments: PortalCommentAttachment[];
};

function Attachments({ attachments }: AttachmentsProps) {
  return (
    <Menu size="medium" className="customMenu">
      {attachments.map((attachment) => (
        <MenuItem
          //key={index}
          tooltipContent=""
          title={attachment.name}
          icon={/\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(attachment.name) ? Image : File}
          onClick={() => {
            const link = document.createElement('a');
            link.href = attachment.public_url;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          className="customMenuItem"
        />
      ))}
    </Menu>
  );
}

type ReplyProps = {
  reply: PortalCommentReply;
};

function Reply({ reply }: ReplyProps) {
  return (
    <Box
      className="client-chat-comment-reply"
      backgroundColor={reply.metadata.type === 'to-client' ? 'primaryBackgroundColor' : 'greyBackgroundColor'}
    >
      <Box padding="medium">
        <Flex gap={4} direction="row">
          <Icon icon={Undo} size={20} style={{ marginRight: '6px', transform: 'scaleX(-1) scaleY(-1) translate3d(0,2px,0)' }} />
          <Text type="text2" color="primary" weight="normal">
            <CommenterName comment={reply} />
          </Text>
          &nbsp;&middot;&nbsp;
          <Text type="text2" color="secondary" weight="normal">
            <RelativeTime date={reply.created_at} />
          </Text>
          {reply.metadata.type === 'from-client' ? (
            <div style={{ marginLeft: '6px' }}>
              <Label text="Client" size="medium" color="primary" />
            </div>
          ) : null}
          <Box style={{ marginLeft: 'auto' }}></Box>
        </Flex>
        {reply.text ? (
          <Text
            type="text2"
            color="primary"
            weight="normal"
            ellipsis={false}
            style={{
              marginTop: '8px',
              marginLeft: '30px',
              whiteSpace: 'pre-line',
            }}
          >
            {reply.text}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

type CommentProps = {
  boardId: number;
  itemId: number;
  comment: PortalComment;
  onAddReply: (comment: string) => Promise<void>;
  index: number;
  length: number;
};

function Comment({ comment, onAddReply, boardId, itemId }: CommentProps) {
  const [showReply, setShowReply] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [isAddingReply, setIsAddingReply] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  return (
    <div className="client-chat-comment-wrapper">
      <Box
        className="client-chat-comment"
        backgroundColor="primaryBackgroundColor"
        style={{
          width: '100%',
        }}
      >
        <Box
          style={{
            margin: '20px 24px',
          }}
        >
          <Flex gap={4} direction="row">
            <Icon icon={Forum} size={24} style={{ marginRight: '4px' }} />
            <Text type="text2" color="primary" weight="medium">
              <CommenterName comment={comment} />
            </Text>
            &nbsp;&middot;&nbsp;
            <Text type="text2" color="secondary" weight="normal">
              <RelativeTime date={comment.created_at} />
            </Text>
            {comment.metadata.type === 'from-client' ? (
              <div style={{ marginLeft: '6px' }}>
                <Label text="Client" color="primary" />
              </div>
            ) : null}
            <Box style={{ marginLeft: 'auto' }}>
              <Tooltip content="Open update" position="top" moveBy={{ main: 9, secondary: 0 }}>
                <IconButton
                  icon={Update}
                  size="small"
                  kind="tertiary"
                  onClick={() => {
                    monday.execute('openLinkInTab', { url: `/boards/${boardId}/pulses/${itemId}/posts/${comment.id}` });
                  }}
                />
              </Tooltip>
            </Box>
          </Flex>
          {comment.text ? (
            <Text
              type="text2"
              color="primary"
              weight="normal"
              ellipsis={false}
              style={{
                marginTop: '8px',
                whiteSpace: 'pre-line',
              }}
            >
              {comment.text}
            </Text>
          ) : null}
        </Box>

        {comment.attachments.length > 0 ? (
          <Box
            marginEnd="large"
            marginBottom="large"
            marginStart="large"
            border={true}
            borderColor="layoutBorderColor"
            rounded="small"
            backgroundColor="greyBackgroundColor"
          >
            <Attachments attachments={comment.attachments} />
          </Box>
        ) : null}
        {comment.replies.length > 0 ? (
          <Box margin="large" border={true} borderColor="layoutBorderColor" rounded="small">
            {comment.replies.map((reply, index) => (
              <>
                <Reply key={reply.id} reply={reply} />
                {index < comment.replies.length - 1 ? <Divider withoutMargin /> : null}
              </>
            ))}
          </Box>
        ) : null}
        <Divider withoutMargin />
        <Box
          padding="medium"
          //padding="small"
          //paddingStart="medium"
          //paddingEnd="medium"
          backgroundColor="greyBackgroundColor"
          style={{
            pointerEvents: isAddingReply ? 'none' : 'all',
            position: 'relative',
          }}
        >
          <Box>
            <div
              style={{
                flex: 1,
                position: 'relative',
              }}
              onClick={() => {
                !showReply && setShowReply(true);
              }}
            >
              <TextArea
                value={replyDraft}
                //placeholder="Type a reply..."
                onChange={(ev) => {
                  setReplyDraft(ev.target.value);
                }}
                autoComplete="off"
                disabled={isAddingReply}
                className={
                  showReply
                    ? 'client-chat-textarea client-chat-textarea--reply'
                    : 'client-chat-textarea client-chat-textarea--reply client-chat-textarea--reply--small'
                }
              />
              {!showReply ? (
                <>
                  <Icon
                    icon={Undo}
                    size={20}
                    style={{
                      pointerEvents: 'none',
                      position: 'absolute',
                      top: '50%',
                      left: '11px',
                      transform: 'scaleX(-1) scaleY(-1) translate3d(0,calc(50% + 1px),0)',
                      color: 'rgba(50, 51, 56, var(--disabled-component-opacity))',
                    }}
                  />
                  <Button
                    kind="tertiary"
                    size="small"
                    color="primary"
                    leftFlat
                    rightFlat
                    disabled={true}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '30px',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    Reply on comment
                  </Button>
                </>
              ) : null}
            </div>

            {showReply ? (
              <Box
                style={{
                  marginTop: '16px',
                }}
              >
                <Flex gap={16}>
                  <EmojiPicker
                    isAddingReply={isAddingReply}
                    replyDraft={replyDraft}
                    setReplyDraft={setReplyDraft}
                    showEmojis={showEmojis}
                    setShowEmojis={setShowEmojis}
                    dialogOffsetX={-10}
                    dialogOffsetY={5}
                  />
                  <Box style={{ marginLeft: 'auto' }}>
                    {!isAddingReply ? (
                      <Flex gap={8}>
                        <Button
                          kind="tertiary"
                          size="medium"
                          color="primary"
                          onClick={() => {
                            setReplyDraft('');
                            setIsAddingReply(false);
                            setShowReply(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          kind="primary"
                          size="medium"
                          color="primary"
                          disabled={!replyDraft || isAddingReply}
                          onClick={async () => {
                            try {
                              setIsAddingReply(true);
                              await onAddReply(replyDraft);
                            } finally {
                              setReplyDraft('');
                              setIsAddingReply(false);
                              setShowReply(false);
                            }
                          }}
                          loading={isAddingReply}
                        >
                          Reply
                        </Button>
                      </Flex>
                    ) : (
                      <Button kind="tertiary" size="xs" disabled={true} loading={isAddingReply} marginLeft={false} marginRight={false}>
                        &nbsp;
                      </Button>
                    )}
                  </Box>
                </Flex>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
      <div className="client-chat-comment-divider"></div>
    </div>
  );
}

type CommentsProps = {
  boardId: number;
  itemId: number;
  comments: PortalComment[];
  onAddComment: (comment: string, files: File[]) => Promise<void>;
  onAddReply: (parentId: number, comment: string) => Promise<void>;
  page: number;
  onPageChange: (page: number) => void;
  commentsPerPage: number;
};

export function Comments({ comments, onAddComment, onAddReply, boardId, itemId }: CommentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);

  let content = null;

  if (!comments || comments.length === 0) {
    // @ts-ignore
    content = (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '24px',
          width: '100%',
          height: 'calc(100vh - (72px + 186px + 48px + 4px))',
          border: '1px dashed #D0D4E4',
          borderRadius: '4px',
        }}
      >
        <Flex
          direction="column"
          gap={16}
          style={{
            marginTop: '-32px',
          }}
        >
          <Flex direction="column" gap={4}>
            <Icon icon={Forum} size={32} />
            <Heading type="h3" weight="bold" align="center">
              No comments
            </Heading>
          </Flex>
          <Text type="text2" align="center" color="secondary">
            Start a new conversation above
            <br />
            or share files with your client.
          </Text>
        </Flex>
      </Box>
    );
  } else {
    // @ts-ignore
    content = comments.map((comment, index) => (
      <Comment
        key={comment.id}
        index={index}
        boardId={boardId}
        itemId={itemId}
        length={comments.length}
        comment={comment}
        onAddReply={async (text) => {
          await onAddReply(+comment.id, text);
        }}
      />
    ));
  }

  return (
    <>
      {/* NEW COMMENT */}

      <Box
        backgroundColor="primaryBackgroundColor"
        shadow="xs"
        style={{
          borderBottom: '1px solid #d0d4e4',
        }}
      >
        <Box
          padding="medium"
          paddingStart="large"
          paddingEnd="large"
          backgroundColor="primaryBackgroundColor"
          style={{
            pointerEvents: isAddingComment ? 'none' : 'all',
          }}
        >
          <TextArea
            value={commentDraft}
            placeholder="Type a comment..."
            onChange={(ev) => {
              setCommentDraft(ev.target.value);
            }}
            autoComplete="off"
            disabled={isAddingComment}
            className="client-chat-textarea"
          />
          <Flex gap={32} style={{ marginTop: '16px' }}>
            <Box
              style={{
                overflow: 'visible',
                flex: 1,
              }}
            >
              <Flex gap={16}>
                <Box>
                  <EmojiPicker
                    isAddingComment={isAddingComment}
                    commentDraft={commentDraft}
                    setCommentDraft={setCommentDraft}
                    showEmojis={showEmojis}
                    setShowEmojis={setShowEmojis}
                    dialogOffsetX={-10}
                    dialogOffsetY={20}
                  />
                </Box>
                {/* UPLOAD ATTACHMENTS */}
                <UploadAttachments fileInputRef={fileInputRef} isAddingComment={isAddingComment} />
              </Flex>
            </Box>
            <Box
              style={{
                marginLeft: 'auto',
              }}
            >
              {!isAddingComment ? (
                <Button
                  kind={!commentDraft ? 'secondary' : 'primary'}
                  size="medium"
                  leftIcon={Forum}
                  disabled={!commentDraft}
                  onClick={async () => {
                    try {
                      setIsAddingComment(true);
                      await onAddComment(commentDraft, Array.from(fileInputRef?.current?.files || []));
                    } finally {
                      // reset the file input
                      if (fileInputRef.current) {
                        const dataTransfer = new DataTransfer();
                        // @ts-ignore
                        fileInputRef.current.files = dataTransfer.files;
                      }
                      setCommentDraft('');
                      setIsAddingComment(false);
                    }
                  }}
                  loading={isAddingComment}
                >
                  Create comment
                </Button>
              ) : (
                <Button kind="tertiary" size="medium" disabled={true} loading={isAddingComment} marginLeft={false} marginRight={false}>
                  &nbsp;
                </Button>
              )}
            </Box>
          </Flex>
        </Box>
      </Box>

      {/* ALL COMMENTS */}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          margin: '0 24px',
        }}
      >
        {content}
      </div>

      {/* PAGINATION */}
      {/* 
      {page > 1 || comments.length === commentsPerPage ? (
        <div>
          {page > 1 && (
            <Button
              kind="secondary"
              size="medium"
              onClick={() => {
                onPageChange(page - 1);
              }}
            >
              Previous
            </Button>
          )}
          {comments.length === commentsPerPage && (
            <Button
              kind="secondary"
              size="medium"
              onClick={() => {
                onPageChange(page + 1);
              }}
            >
              Next
            </Button>
          )}
        </div>
      ) : null}
       */}
    </>
  );
}
